/**
 * Included-tax display for the order page (#TAG-3345).
 *
 * Mirrors `packages/plugin-sdk/src/v2/core/utils/includedTax.ts`, which this app
 * cannot import. When a store's tax rate is "included in price", the price engine
 * takes the tax out of each line's `adjustedAmount` (300 → 285.71) and adds it
 * back as a Tax adjustment flagged `included: true`; it records what it removed
 * as `includedTaxAmount` on each summary item and on each order-level promotion
 * it scaled. These helpers fold it back: lines at their shelf price, the
 * discount the merchant set, a tax figure holding only tax added on top, and the
 * included tax listed apart ("Includes 14.29 VAT"). The total is never touched.
 *
 * A summary without an included Tax adjustment (every store without such a rate,
 * every order placed before the engine set the flag) comes back unchanged.
 */

export interface IncludedTaxLine {
  /** The tax rate's name as the merchant wrote it ("VAT", "TVA", "MwSt"). */
  description: string;
  amount: number;
}

interface SummaryAdjustmentLike {
  type?: string | null;
  amount?: number;
  description?: string | null;
  included?: boolean;
  includedTaxAmount?: number;
}

interface SummaryItemLike {
  variantId?: string | null;
  priceId?: string | null;
  quantity?: number;
  amount?: number;
  adjustedAmount?: number;
  includedTaxAmount?: number;
  currency?: string | null;
}

interface IncludedTaxSummary<A extends SummaryAdjustmentLike> {
  items?: ReadonlyArray<SummaryItemLike> | null;
  adjustments?: A[];
  subtotalAdjustedAmount?: number;
  totalTaxAmount?: number;
  totalPromotionAmount?: number;
}

interface LineItemLike {
  id: string;
  variantId?: string | null;
  priceId?: string | null;
  quantity: number;
  amount: number;
  adjustedAmount: number;
  currency?: string | null;
}

const isIncludedTax = (adjustment: SummaryAdjustmentLike): boolean =>
  adjustment.type === 'Tax' && adjustment.included === true;

const sumIncludedTax = (entries: ReadonlyArray<{ includedTaxAmount?: number }>): number =>
  entries.reduce((sum, entry) => sum + (entry.includedTaxAmount ?? 0), 0);

export function getIncludedTaxes(
  adjustments: ReadonlyArray<SummaryAdjustmentLike> | undefined,
): IncludedTaxLine[] {
  return (adjustments ?? []).filter(isIncludedTax).map((adjustment) => ({
    description: adjustment.description ?? '',
    amount: adjustment.amount ?? 0,
  }));
}

export function toIncludedTaxDisplay<A extends SummaryAdjustmentLike, T extends IncludedTaxSummary<A>>(
  summary: T,
): T & { includedTaxes: IncludedTaxLine[] } {
  // Already folded: a second pass would add the tax back twice.
  if ('includedTaxes' in summary && Array.isArray(summary.includedTaxes)) {
    return summary as T & { includedTaxes: IncludedTaxLine[] };
  }
  const includedTaxes = getIncludedTaxes(summary.adjustments);
  if (includedTaxes.length === 0) return { ...summary, includedTaxes };

  const promotions = (summary.adjustments ?? []).filter((adjustment) => adjustment.type === 'Promotion');
  const includedTaxTotal = includedTaxes.reduce((sum, line) => sum + line.amount, 0);

  // Order-level promotion amounts are negative; the engine shrank them by `includedTaxAmount`.
  const adjustments = summary.adjustments?.map((adjustment) =>
    adjustment.type === 'Promotion' && adjustment.includedTaxAmount
      ? { ...adjustment, amount: (adjustment.amount ?? 0) - adjustment.includedTaxAmount }
      : adjustment,
  );

  return {
    ...summary,
    ...(adjustments && { adjustments }),
    ...(summary.subtotalAdjustedAmount !== undefined && {
      subtotalAdjustedAmount: summary.subtotalAdjustedAmount + sumIncludedTax(summary.items ?? []),
    }),
    ...(summary.totalPromotionAmount !== undefined && {
      totalPromotionAmount: summary.totalPromotionAmount + sumIncludedTax(promotions),
    }),
    ...(summary.totalTaxAmount !== undefined && {
      totalTaxAmount: summary.totalTaxAmount - includedTaxTotal,
    }),
    includedTaxes,
  };
}

const lineKey = (line: SummaryItemLike): string =>
  [line.variantId ?? '', line.priceId ?? '', line.quantity, line.amount, line.adjustedAmount].join('|');

// An order holds one row per summary currency: the same numeric price in two
// currencies must not borrow the other's tax. Compared only when both sides carry one.
const sameCurrency = (a?: string | null, b?: string | null): boolean => !a || !b || a === b;

/**
 * Included tax of each order line item, by line item id. `order_line_items` rows
 * do not carry `includedTaxAmount`; they are copies of the summary items
 * (`updateOrderWithCheckoutSessionInternal`), with no id linking the two, so a
 * row is paired with the first unused summary item holding the same variant,
 * price, quantity and amounts. Only summaries with an included Tax adjustment
 * are read; a row without a pair gets nothing and keeps its stored amounts.
 */
export function getLineItemIncludedTax(
  lineItems: ReadonlyArray<LineItemLike>,
  summaries: ReadonlyArray<IncludedTaxSummary<SummaryAdjustmentLike>>,
): Map<string, number> {
  const pool = summaries
    .filter((summary) => getIncludedTaxes(summary.adjustments).length > 0)
    .flatMap((summary) => summary.items ?? [])
    .filter((item) => (item.includedTaxAmount ?? 0) !== 0)
    .map((item) => ({ key: lineKey(item), currency: item.currency, includedTaxAmount: item.includedTaxAmount ?? 0 }));

  const usedIndexes = new Set<number>();
  return new Map(
    lineItems.flatMap((lineItem): [string, number][] => {
      const key = lineKey(lineItem);
      const index = pool.findIndex(
        (entry, i) => !usedIndexes.has(i) && entry.key === key && sameCurrency(entry.currency, lineItem.currency),
      );
      if (index === -1) return [];
      usedIndexes.add(index);
      return [[lineItem.id, pool[index].includedTaxAmount]];
    }),
  );
}
